import React from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    Grid,
    Chip
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    Cell
} from 'recharts';
import { Feedback, ThumbUp, ThumbDown } from '@mui/icons-material';

const FeedbackAnalyticsChart = ({ feedbackMetrics = {}, loading = false }) => {
    // Generate feedback trend data based on REAL feedback dates from your DB
    const generateFeedbackTrend = () => {
        const feedbackData = feedbackMetrics.feedbackData || [];

        if (!feedbackData.length) {
            return [];
        }

        // Group feedback by week based on actual creation dates
        const weeklyData = {};

        feedbackData.forEach(feedback => {
            const createdDate = new Date(feedback.createdAt);
            const weekStart = new Date(createdDate);
            weekStart.setDate(createdDate.getDate() - createdDate.getDay()); // Start of week (Sunday)

            const weekKey = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            if (!weeklyData[weekKey]) {
                weeklyData[weekKey] = {
                    week: weekKey,
                    positive: 0,
                    negative: 0,
                    total: 0,
                    startDate: weekStart
                };
            }

            weeklyData[weekKey].total++;
            if (feedback.correct) {
                weeklyData[weekKey].positive++;
            } else {
                weeklyData[weekKey].negative++;
            }
        });

        // Convert to array and sort by date
        return Object.values(weeklyData)
            .sort((a, b) => a.startDate - b.startDate)
            .slice(-4); // Last 4 weeks
    };

    // Generate feedback type distribution based on REAL data
    const generateFeedbackTypes = () => {
        const total = feedbackMetrics.totalFeedbacks || 0;
        const positive = feedbackMetrics.positiveFeedbacks || 0;
        const negative = feedbackMetrics.negativeFeedbacks || 0;
        const open = feedbackMetrics.openFeedbacks || 0;
        const resolved = feedbackMetrics.resolvedFeedbacks || 0;
        const reviewed = feedbackMetrics.reviewedFeedbacks || 0;

        return [
            {
                type: 'Positive',
                count: positive,
                percentage: total > 0 ? (positive / total * 100).toFixed(1) : 0,
                color: '#4caf50',
                icon: <ThumbUp sx={{ fontSize: 16 }} />
            },
            {
                type: 'Negative',
                count: negative,
                percentage: total > 0 ? (negative / total * 100).toFixed(1) : 0,
                color: '#f44336',
                icon: <ThumbDown sx={{ fontSize: 16 }} />
            },
            {
                type: 'Open',
                count: open,
                percentage: total > 0 ? (open / total * 100).toFixed(1) : 0,
                color: '#ff9800',
                icon: null
            },
            {
                type: 'Resolved',
                count: resolved,
                percentage: total > 0 ? (resolved / total * 100).toFixed(1) : 0,
                color: '#2196f3',
                icon: null
            },
            {
                type: 'Reviewed',
                count: reviewed,
                percentage: total > 0 ? (reviewed / total * 100).toFixed(1) : 0,
                color: '#9c27b0',
                icon: null
            }
        ].filter(item => item.count > 0); // Only show types that have data
    };

    const trendData = generateFeedbackTrend();
    const typeData = generateFeedbackTypes();
    const totalFeedbacks = feedbackMetrics.totalFeedbacks || 0;
    const positiveFeedbacks = feedbackMetrics.positiveFeedbacks || 0;
    const satisfactionRate = totalFeedbacks > 0 ? (positiveFeedbacks / totalFeedbacks * 100).toFixed(1) : 0;

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box sx={{
                    background: 'white',
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    boxShadow: 2
                }}>
                    <Typography variant="body2" fontWeight={600}>
                        Week of {label}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                            {entry.dataKey}: {entry.value}
                        </Typography>
                    ))}
                </Box>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Card elevation={3} sx={{ borderRadius: 3, height: 450 }}>
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress sx={{ mb: 2 }} />
                        <Typography>Loading feedback analytics...</Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={3} sx={{ borderRadius: 3, height: 450 }}>
            <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Feedback sx={{ color: '#ff9800', mr: 1, fontSize: 28 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
                            Feedback Analytics Overview
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Total Feedback: {totalFeedbacks} | Satisfaction: {satisfactionRate}%
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={2} sx={{ height: 350 }}>
                    {/* Weekly Trend Chart */}
                    <Grid item xs={12} md={7}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            Weekly Feedback Trend
                        </Typography>
                        {trendData.length === 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                                <Typography color="text.secondary">No feedback trend data available</Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={trendData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="week"
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="positive" fill="#4caf50" name="Positive" />
                                    <Bar dataKey="negative" fill="#f44336" name="Negative" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Grid>

                    {/* Status Distribution */}
                    <Grid item xs={12} md={5}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            Status Distribution
                        </Typography>
                        <Box sx={{ height: 250, overflowY: 'auto' }}>
                            {typeData.length === 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Typography color="text.secondary">No feedback data available</Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={1}>
                                    {typeData.map((type, index) => (
                                        <Grid item xs={12} key={type.type}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 1,
                                                border: `1px solid ${type.color}20`,
                                                borderRadius: 1,
                                                backgroundColor: `${type.color}08`
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {type.icon}
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {type.type}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {type.count} items ({type.percentage}%)
                                                    </Typography>
                                                    <Typography variant="h6" fontWeight={700} sx={{ color: type.color }}>
                                                        {type.count}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default FeedbackAnalyticsChart;
